// src/contexts/AppContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useMsal } from "@azure/msal-react";
import { createGraphService } from "../services/graphService";
import { runExpiryCheck } from "../components/common/ExpiryReminder";
import { monitorObligations } from "../services/obligationService";
import { initializeDocumentMetadata } from "../services/documentMetadataService";

const AppContext = createContext({});
export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const { instance, accounts } = useMsal();
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // "admin" | "user" | null
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [expiringContracts, setExpiringContracts] = useState([]);
  const [expiryChecked, setExpiryChecked] = useState(false);
  const [contractTypes, setContractTypes] = useState([]);

  // Create service once and memoize it to avoid recreating on every render
  const service = React.useMemo(() => createGraphService(instance), [instance]);

  const loadCurrentUser = useCallback(async () => {
    if (!accounts?.length) return;
    try {
      setLoading(true);
      const me = await service.getCurrentUser();
      setCurrentUser(me);

      // Check AllowedUsers list — fetch all and match case-insensitively
      // to avoid OData filter case-sensitivity issues with dotted email addresses
      const userEmail = (me.mail || me.userPrincipalName || "").toLowerCase();
      const allowedData = await service
        .getListItems("AllowedUsers")
        .catch(() => null);
      const match = (allowedData?.value || []).find(
        (i) => (i.fields?.Title || "").toLowerCase() === userEmail,
      );

      if (!match) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }
      const role = match.fields.Role || "user";
      setUserRole(role);
      setAccessDenied(false);

      // Initialize DocumentMetadata list if it doesn't exist (once per session)
      initializeDocumentMetadata(service)
        .catch((err) => console.warn("DocumentMetadata initialization failed:", err));

      // Run expiry check once per session for admins
      if (role === "admin" && !expiryChecked) {
        setExpiryChecked(true);
        runExpiryCheck(service, me, true)
          .then((expiring) => setExpiringContracts(expiring))
          .catch(() => {});
      }
    } catch (e) {
      console.error("User load error", e);

      setUserRole("user");
      setAccessDenied(true);
    } finally {
      setLoading(false);
    }
  }, [accounts, service]);

  useEffect(() => {
    loadCurrentUser();
  }, [loadCurrentUser]);

  // Fetch contract types from uploaded templates — runs once on mount after loading completes
  useEffect(() => {
    if (loading) return;
    (async () => {
      try {
        const drives = await service.getDrives().catch(() => null);
        if (!drives?.value) return;
        const drive = drives.value.find((d) => d.name === "ContractDocuments");
        if (!drive) return;
        const contents = await service.getFolderContents(drive.id, "Templates");
        const types = (contents?.value || [])
          .filter((i) => !i.folder && i.name?.toLowerCase().endsWith(".docx"))
          .map((i) => i.name.replace(/\.docx$/i, ""))
          .sort();
        setContractTypes(types);
      } catch (e) {
        console.warn("Could not load contract types:", e);
      }
    })();
  }, [loading, service]);

  // Background expiry check timer for admins (every hour)
  useEffect(() => {
    if (!userRole || userRole !== "admin" || loading || !currentUser) return;

    // Run check immediately on mount
    runExpiryCheck(service, currentUser, true)
      .then((expiring) => setExpiringContracts(expiring))
      .catch((err) => console.warn("Background expiry check failed:", err));

    // Set up timer to run every hour
    const timerInterval = setInterval(() => {
      runExpiryCheck(service, currentUser, true)
        .then((expiring) => setExpiringContracts(expiring))
        .catch((err) => console.warn("Background expiry check failed:", err));
    }, 3600000); // 1 hour

    // Cleanup on unmount
    return () => clearInterval(timerInterval);
  }, [userRole, currentUser, service, loading]);

  // Background obligation monitoring timer for admins (every 30 minutes)
  useEffect(() => {
    if (!userRole || userRole !== "admin" || loading || !currentUser) return;

    const runObligationCheck = async () => {
      try {
        await monitorObligations(service);
      } catch (err) {
        console.warn("Background obligation check failed:", err);
      }
    };

    // Run check immediately on mount
    runObligationCheck();

    // Set up timer to run every 30 minutes
    const timerInterval = setInterval(runObligationCheck, 1800000);

    // Cleanup on unmount
    return () => clearInterval(timerInterval);
  }, [userRole, currentUser, service, loading]);

  const addNotification = (msg, type = "info") => {
    const n = { id: Date.now(), msg, type };
    setNotifications((prev) => [n, ...prev.slice(0, 9)]);
    setTimeout(
      () => setNotifications((prev) => prev.filter((x) => x.id !== n.id)),
      6000,
    );
  };

  return (
    <AppContext.Provider
      value={{
        currentUser,
        userRole,
        accessDenied,
        loading,
        contracts,
        setContracts,
        notifications,
        addNotification,
        expiringContracts,
        contractTypes,
        service,
        instance,
        isAdmin: userRole === "admin",
        isUser: userRole === "user",
        reload: loadCurrentUser,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
